import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { SEO } from "@/components/SEO";
import { motion, useInView, useMotionValue, useTransform, animate, useScroll, useSpring } from "framer-motion";
import {
  ArrowRight, Buildings, UsersThree, ChartLineUp, CalendarCheck,
  Target, Handshake, ShieldCheck,
  Lightning, Sparkle, Storefront, Certificate, ChartBar,
  Star
} from "@phosphor-icons/react";

const LOGO_URL = "/bch-logo.png";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/uz62qvb6_1%20variation.png",
  meeting: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/z38x9i2u_2%20variation.png",
  handshake: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/1q4conde_3%20variation.png",
  team: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/kvv97gq2_4%20variation.png",
  conference: "https://customer-assets.emergentagent.com/job_b68f5d48-9358-4d0a-afc5-51cf7740066b/artifacts/zfaycjjc_5%20variation.png",
};

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const fadeUpDelay = (d) => ({ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } } });
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const scaleUp = { hidden: { opacity: 0, scale: 0.92 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };

function AnimatedCounter({ target, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.floor(v));
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (inView) {
      const controls = animate(count, target, { duration, ease: "easeOut" });
      const unsub = rounded.on("change", v => setDisplay(v.toLocaleString()));
      return () => { controls.stop(); unsub(); };
    }
  }, [inView, count, target, duration, rounded]);
  return <span ref={ref}>{display}{suffix}</span>;
}

/* Floating particle */
function FloatingOrb({ size, color, x, y, delay, dur }) {
  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{ width: size, height: size, background: color, left: x, top: y, filter: `blur(${size > 60 ? 40 : 20}px)` }}
      animate={{ y: [0, -30, 0, 20, 0], x: [0, 15, -10, 5, 0], scale: [1, 1.15, 0.95, 1.08, 1] }}
      transition={{ duration: dur, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );
}

/* Animated word reveal */
function WordReveal({ text, className, style, delay = 0 }) {
  const words = text.split(" ");
  return (
    <span className={className} style={style}>
      {words.map((word, i) => (
        <motion.span key={i} className="inline-block mr-[0.3em]"
          initial={{ opacity: 0, y: 30, rotateX: -40 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.6, delay: delay + i * 0.08, ease: [0.22, 1, 0.36, 1] }}>
          {word}
        </motion.span>
      ))}
    </span>
  );
}

/* Shimmer badge */
const shimmerKeyframes = `
@keyframes shimmer-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
@keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 20px rgba(26,138,109,0.15); } 50% { box-shadow: 0 0 40px rgba(26,138,109,0.3); } }
@keyframes float-gentle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes rotate-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useSpring(useTransform(scrollYProgress, [0, 1], [0, 80]), { stiffness: 100, damping: 30 });
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.05]);

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: "#FAFAF8", fontFamily: "'Manrope', sans-serif", color: "#1a1a1a" }} data-testid="home-page">
      <style>{shimmerKeyframes}</style>
      <SEO title="Home" description="BCH Networking is India's premier closed-network for verified business owners. 500+ active members across 3+ cities exchanging referrals, attending bi-weekly meetings, and scaling businesses together." path="/" />

      {/* ===== HERO — Full-width cinematic background ===== */}
      <section ref={heroRef} className="relative min-h-[80vh] sm:min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Full background image with parallax */}
        <motion.div className="absolute inset-0 z-0"
          style={{ y: heroImgY, scale: heroScale }}>
          <img src={IMAGES.hero} alt="Business professionals networking"
            className="w-full h-full object-cover" loading="eager"
            style={{ filter: "brightness(0.35)" }} />
        </motion.div>

        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(135deg, rgba(10,10,10,0.7) 0%, rgba(26,138,109,0.25) 50%, rgba(10,10,10,0.6) 100%)" }} />

        {/* Subtle animated grid */}
        <div className="absolute inset-0 pointer-events-none z-[2] opacity-[0.04]">
          <div style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)", backgroundSize: "80px 80px", height: "100%", width: "100%" }} />
        </div>

        {/* Floating orbs on the background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
          <FloatingOrb size={200} color="rgba(26,138,109,0.12)" x="5%" y="20%" delay={0} dur={8} />
          <FloatingOrb size={140} color="rgba(212,167,58,0.1)" x="80%" y="15%" delay={1.5} dur={10} />
          <FloatingOrb size={100} color="rgba(26,138,109,0.08)" x="65%" y="70%" delay={3} dur={7} />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-28 w-full">
          <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="max-w-3xl">
            {/* Badge with shimmer */}
            <motion.div initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full mb-6 sm:mb-10 relative overflow-hidden"
              style={{ background: "rgba(26,138,109,0.15)", border: "1px solid rgba(26,138,109,0.3)", backdropFilter: "blur(12px)" }}>
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, transparent, rgba(26,138,109,0.2), transparent)", animation: "shimmer-slide 3s infinite" }} />
              <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }}>
                <Sparkle className="w-4 h-4 text-[#4dd4a8]" weight="fill" />
              </motion.div>
              <span className="text-xs font-semibold tracking-wider uppercase text-[#4dd4a8] relative z-10">India's Premier Business Network</span>
            </motion.div>

            {/* Large heading */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 sm:mb-8 leading-[1.08]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <WordReveal text="Where Business" delay={0.3} className="text-white" />
              <br />
              <WordReveal text="Owners" delay={0.55} className="text-white" />
              {" "}
              <span className="relative inline-block">
                <motion.span style={{ color: "#4dd4a8" }}
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}>
                  Build Together
                </motion.span>
                <motion.div className="absolute -bottom-2 left-0 right-0 h-[4px] rounded-full origin-left" style={{ background: "linear-gradient(90deg, #d4a73a, #4dd4a8)" }}
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, delay: 1.2, ease: [0.22, 1, 0.36, 1] }} />
              </span>
            </h1>

            {/* Subtitle */}
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
              className="text-base md:text-lg lg:text-xl max-w-2xl mb-8 sm:mb-12 leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
              A chapter-based networking ecosystem. Exchange referrals, track growth, and scale through{" "}
              <motion.span className="font-semibold relative"
                initial={{ backgroundSize: "0% 2px" }} animate={{ backgroundSize: "100% 2px" }}
                transition={{ duration: 1, delay: 1.6 }}
                style={{ color: "#d4a73a", backgroundImage: "linear-gradient(#d4a73a, #d4a73a)", backgroundRepeat: "no-repeat", backgroundPosition: "0 100%", paddingBottom: 2 }}>
                measurable outcomes
              </motion.span>.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.1 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12">
              <Link to="/signup" data-testid="hero-apply-btn"
                className="group relative rounded-full px-8 sm:px-10 py-4 sm:py-5 font-bold text-sm sm:text-base transition-all duration-300 flex items-center justify-center gap-3 hover:-translate-y-1 hover:shadow-2xl"
                style={{ background: "#1a8a6d", color: "#fff", animation: "pulse-glow 3s infinite" }}>
                <span className="relative z-10 flex items-center gap-2">
                  Apply for Membership <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" weight="bold" />
                </span>
              </Link>
              <Link to="/about"
                className="group rounded-full px-8 sm:px-10 py-4 sm:py-5 font-semibold text-sm sm:text-base transition-all duration-300 text-center hover:-translate-y-1"
                style={{ border: "2px solid rgba(255,255,255,0.4)", color: "#fff", backdropFilter: "blur(8px)", background: "rgba(255,255,255,0.06)" }}>
                <span className="relative z-10">Learn More</span>
              </Link>
            </motion.div>

            {/* Trust strip */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }}
              className="flex items-center gap-5 flex-wrap">
              <div className="flex -space-x-2">
                {[0,1,2,3].map(i => (
                  <motion.div key={i} initial={{ scale: 0, x: -10 }} animate={{ scale: 1, x: 0 }} transition={{ delay: 1.6 + i * 0.1 }}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2"
                    style={{ background: ["#1a8a6d", "#d4a73a", "#2aaa8a", "#e8b84a"][i], color: "#fff", borderColor: "rgba(255,255,255,0.2)", zIndex: 4 - i }}>
                    {["R","P","A","M"][i]}
                  </motion.div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-4 h-4 text-[#d4a73a]" weight="fill" />)}
                </div>
                <p className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>Trusted by 1000+ business owners</p>
              </div>
            </motion.div>
          </div>

          {/* Right side — Vertical stat cards (desktop only) */}
          <div className="hidden lg:flex flex-col gap-4">
            {[
              { icon: UsersThree, value: 1000, suffix: "+", label: "Active Members", color: "#1a8a6d" },
              { icon: Buildings, value: 3, suffix: "+", label: "Cities", color: "#d4a73a" },
              { icon: ChartLineUp, value: 50, suffix: "Cr+", label: "Business Exchanged", color: "#1a8a6d" },
              { icon: CalendarCheck, value: 1000, suffix: "+", label: "Referrals Passed", color: "#d4a73a" }
            ].map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: 60, y: 20 }}
                animate={{ opacity: 1, x: 0, y: [0, -6, 0, 4, 0] }}
                transition={{ opacity: { delay: 1.2 + i * 0.2, duration: 0.6 }, x: { delay: 1.2 + i * 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }, y: { delay: 2 + i * 0.5, duration: 4 + i * 0.5, repeat: Infinity, ease: "easeInOut" } }}
                whileHover={{ scale: 1.1, y: -10, rotateY: 6, rotateX: -4, background: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.4)", boxShadow: "0 24px 48px rgba(0,0,0,0.3), 0 0 40px rgba(26,138,109,0.2)", transition: { duration: 0.3 } }}
                className="flex flex-col items-center text-center py-5 px-6 rounded-2xl backdrop-blur-md w-44 cursor-default"
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}>
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.3, ease: "easeInOut" }}
                  whileHover={{ rotate: [0, -15, 15, -8, 0], scale: [1, 1.4, 1.2], transition: { duration: 0.5 } }}>
                  <s.icon className="w-6 h-6 mb-2" style={{ color: s.color === "#1a8a6d" ? "#4dd4a8" : "#d4a73a" }} weight="duotone" />
                </motion.div>
                <p className="text-xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {s.suffix.includes("Cr") ? <>&#8377;<AnimatedCounter target={s.value} />{s.suffix.replace(String(s.value), "")}</> : <AnimatedCounter target={s.value} suffix={s.suffix} />}
                </p>
                <p className="text-[10px] tracking-[0.15em] uppercase font-medium mt-1 text-white/50">{s.label}</p>
              </motion.div>
            ))}
          </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5 }}
          className="absolute bottom-24 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 hidden sm:flex">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
            className="w-7 h-11 rounded-full flex items-start justify-center pt-2"
            style={{ border: "2px solid rgba(255,255,255,0.25)" }}>
            <motion.div className="w-1.5 h-1.5 rounded-full bg-white/60"
              animate={{ y: [0, 14, 0], opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
          </motion.div>
        </motion.div>
      </section>

      {/* STATS BAR — Vertical Cards (mobile/tablet only) */}
      <section className="py-14 md:py-20 lg:hidden" style={{ background: "#f2f2ee" }} data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={stagger}
            className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: UsersThree, value: 500, suffix: "+", label: "Active Members", color: "#1a8a6d" },
              { icon: Buildings, value: 3, suffix: "+", label: "Cities", color: "#d4a73a" },
              { icon: ChartLineUp, value: 50, suffix: "Cr+", label: "Business Exchanged", color: "#1a8a6d" },
              { icon: CalendarCheck, value: 1000, suffix: "+", label: "Referrals Passed", color: "#d4a73a" }
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }}
                className="flex flex-col items-center justify-center text-center py-10 md:py-14 px-4 rounded-3xl transition-all duration-300 group cursor-default"
                style={{ background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <s.icon className="w-7 h-7 mb-5 group-hover:scale-110 transition-transform" style={{ color: s.color }} weight="duotone" />
                <p className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: s.color }}>
                  {s.suffix.includes("Cr") ? <>&#8377;<AnimatedCounter target={s.value} />{s.suffix.replace(String(s.value), "")}</> : <AnimatedCounter target={s.value} suffix={s.suffix} />}
                </p>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: "#999" }}>{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-10 md:py-14" style={{ background: "#FAFAF8" }} data-testid="how-it-works">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 text-center font-semibold" style={{ color: "#1a8a6d" }}>The Process</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight text-center" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
              Four Steps to <span style={{ color: "#1a8a6d" }}>Business Growth</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative mt-10">
              <div className="hidden md:block absolute top-14 left-[12%] right-[12%] h-px" style={{ background: "linear-gradient(90deg, transparent, #c5e8dc, #f0d9a0, transparent)" }} />
              {[
                { n: "01", title: "Join a Chapter", desc: "One category per chapter. Zero competition.", icon: Buildings, color: "#1a8a6d" },
                { n: "02", title: "Attend Meetings", desc: "Two structured sessions per month for referrals.", icon: CalendarCheck, color: "#d4a73a" },
                { n: "03", title: "Exchange Referrals", desc: "Share genuine business opportunities.", icon: Handshake, color: "#1a8a6d" },
                { n: "04", title: "Track Growth", desc: "Measure every outcome in real-time.", icon: ChartBar, color: "#d4a73a" }
              ].map((step, i) => (
                <motion.div key={i} variants={fadeUp} whileHover={{ y: -8 }}
                  className="relative p-6 rounded-2xl text-center group transition-all duration-300"
                  style={{ background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
                  <div className="w-14 h-14 mx-auto mb-5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                    style={{ background: step.color === "#1a8a6d" ? "#e8f5f0" : "#fef8ec" }}>
                    <step.icon className="w-7 h-7" style={{ color: step.color }} weight="duotone" />
                  </div>
                  <span className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: step.color }}>{step.n}</span>
                  <h3 className="text-lg font-semibold mt-2 mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>{step.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#777" }}>{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* IMAGE + FEATURES SPLIT */}
      <section className="py-10 md:py-14" style={{ background: "#f2f2ee" }} data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image */}
            <motion.div variants={scaleUp} className="relative order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-xl aspect-[16/10] sm:aspect-[4/3]">
                <img src={IMAGES.meeting} alt="Business meeting" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="absolute -bottom-5 -right-5 p-5 rounded-2xl shadow-xl hidden lg:block"
                style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#e8f5f0" }}>
                    <ShieldCheck className="w-5 h-5 text-[#1a8a6d]" weight="duotone" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: "#111" }}>Category Protected</p>
                    <p className="text-[10px]" style={{ color: "#999" }}>Zero internal competition</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Features list */}
            <div className="order-1 lg:order-2">
              <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 font-semibold" style={{ color: "#d4a73a" }}>Why We're Different</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
                Built for <span style={{ color: "#1a8a6d" }}>Serious</span> Business Owners
              </motion.h2>
              <motion.div variants={stagger} className="space-y-4">
                {[
                  { icon: ShieldCheck, title: "Category Exclusivity", desc: "One business per category. Your market is protected.", bg: "#e8f5f0", color: "#1a8a6d" },
                  { icon: Target, title: "Accountability", desc: "Track every promise, every referral, every outcome.", bg: "#fef8ec", color: "#d4a73a" },
                  { icon: ChartLineUp, title: "Measurable ROI", desc: "Real-time dashboards showing actual business growth.", bg: "#e8f5f0", color: "#1a8a6d" },
                  { icon: Storefront, title: "Partner Discounts", desc: "Exclusive savings at BCH partner stores.", bg: "#fef8ec", color: "#d4a73a" },
                  { icon: Certificate, title: "Official Testimonials", desc: "Downloadable A4 testimonials from fellow members.", bg: "#e8f5f0", color: "#1a8a6d" }
                ].map((f, i) => (
                  <motion.div key={i} variants={fadeUp} whileHover={{ x: 6 }}
                    className="flex items-start gap-4 p-4 rounded-2xl transition-all duration-300 group"
                    style={{ background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.04)" }}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: f.bg }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} weight="duotone" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold mb-0.5" style={{ color: "#111" }}>{f.title}</h3>
                      <p className="text-sm" style={{ color: "#777" }}>{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MEMBER BENEFITS with image */}
      <section className="py-10 md:py-14" style={{ background: "#FAFAF8" }} data-testid="benefits-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <motion.p variants={fadeUp} className="text-xs tracking-[0.3em] uppercase mb-3 font-semibold" style={{ color: "#1a8a6d" }}>Membership</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", color: "#111" }}>
                What You <span style={{ color: "#d4a73a" }}>Gain</span>
              </motion.h2>
              <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Handshake, title: "Bi-Weekly Meetings", color: "#1a8a6d" },
                  { icon: ChartLineUp, title: "Growth Analytics", color: "#d4a73a" },
                  { icon: Storefront, title: "Store Discounts", color: "#1a8a6d" },
                  { icon: Certificate, title: "Testimonials", color: "#d4a73a" },
                  { icon: UsersThree, title: "Trusted Network", color: "#1a8a6d" }
                ].map((b, i) => (
                  <motion.div key={i} variants={fadeUp} whileHover={{ y: -4 }}
                    className="p-4 rounded-2xl transition-all duration-300 flex items-center gap-3 group"
                    style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.04)", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
                    <b.icon className="w-6 h-6 flex-shrink-0 group-hover:scale-110 transition-transform" style={{ color: b.color }} weight="duotone" />
                    <span className="text-sm font-semibold" style={{ color: "#333" }}>{b.title}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
            <motion.div variants={scaleUp} className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl aspect-[16/10] sm:aspect-[4/3]">
                <img src={IMAGES.team} alt="Business team collaboration" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 relative overflow-hidden" data-testid="cta-section"
        style={{ background: "linear-gradient(135deg, #1a8a6d 0%, #147058 100%)" }}>
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)`, backgroundSize: "60px 60px", height: "100%", width: "100%" }} />
        </div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-5xl font-medium tracking-tight mb-4 sm:mb-6" style={{ fontFamily: "'Outfit', sans-serif", color: "#fff" }}>
            Ready to Grow with BCH?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-sm sm:text-base md:text-lg mb-8 sm:mb-10 leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
            Join a network where business is generated, tracked, and scaled — not just discussed.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup"
              className="group w-full sm:w-auto rounded-full px-10 py-4 font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 hover:-translate-y-1 hover:shadow-2xl"
              style={{ background: "#d4a73a", color: "#111" }} data-testid="cta-apply-btn">
              Apply for Membership <Lightning className="w-4 h-4 group-hover:rotate-12 transition-transform" weight="fill" />
            </Link>
            <Link to="/about"
              className="w-full sm:w-auto rounded-full px-10 py-4 font-semibold text-sm transition-all duration-300 hover:-translate-y-1"
              style={{ border: "2px solid rgba(255,255,255,0.5)", color: "#fff" }}>
              Explore Chapters
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: "#fff", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <img src={LOGO_URL} alt="BCH" className="h-12 sm:h-16 w-auto" />
              <span className="text-xs sm:text-sm" style={{ color: "#999" }}>Connect &middot; Collaborate &middot; Grow</span>
            </div>
            <div className="flex items-center gap-6 sm:gap-8 text-xs sm:text-sm" style={{ color: "#777" }}>
              {["/", "/about", "/login", "/signup"].map((p, i) => (
                <Link key={p} to={p} className="hover:text-[#1a8a6d] transition-colors">{["Home", "About", "Login", "Apply"][i]}</Link>
              ))}
            </div>
          </div>
          <div className="mt-6 pt-4 text-center" style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <p className="text-xs" style={{ color: "#bbb" }}>&copy; 2026 BCH Networking. All rights reserved.</p>
            <p className="text-xs mt-2" style={{ color: "#ccc" }}>Website built with code, coffee, and a lot of logic by <a href="https://kensworth.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#1a8a6d] transition-colors" style={{ color: "#999", fontWeight: 600 }}>Kensworth.in</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
